import { Application, Container, Graphics, Matrix, Sprite, Text, Texture } from 'pixi.js';
import { cachedBitmap, loadCraterMask, sharedArt } from './art-cache';
import { loadBitmap, type AssetCatalog } from './assets';
import { aimAngle, aimTicks, polar } from './aim';
import { Camera, cameraFocus, type Point, type Rect } from './camera';
import { CharacterAnimator } from './character-animator';
import { skinKey, gameLayerPaths, type PlayerLook } from './character-sheet';
import { CharacterSkin } from './character-skin';
import type { BombArtLibrary } from './bomb-art';
import { BLAST_FPS } from './bomb-art';
import { ballsOfWeapon } from './weapon-balls';
import type { WingArtLibrary } from './wing-art';
import { WING_ANCHOR, type WingSide } from './wing-art';
import { WING_FPS } from './wing-show';
import { UiAtlas } from './ui-atlas';
import { ballSpin, spinDegrees } from './ball-blastout';
import { DAMAGE_TEXT_STYLES, damageTexts, healText, type DamageText } from './damage-text';
import { FALLBACK_CRATER_RADIUS, GroundSampler } from './ground';
import type { TerrainMask } from './mask';
import { Minimap, TEAM_COLORS, type MinimapSource } from './minimap';
import { PROJECTILE_SPEED, type CombatantView, type CraterInfo, type GameState, type StateEvent } from './state';
import { positionAt } from './timing';
import { fallTomb, TOMB_IMAGE_OFFSET, TOMB_WAKE_RADIUS } from './tomb';
import { BALL_FLYING, EMITTERS } from './ball-particles';
import type { ParticleArtLibrary } from './particle-art';
import { PARTICLE_STEP, ParticleSystem, type Emitter } from './particles';

export interface RendererOptions {
  canvas: HTMLCanvasElement;
  assets: AssetCatalog | null;
  state: GameState;
  /** Elemento cujo tamanho o canvas acompanha (padrão: o pai do canvas). */
  resizeTarget?: HTMLElement;
  /** Recebe cada evento do estado (impacto, fim de tiro) para a interface tocar sons, mostrar avisos etc. */
  onEvent?: (event: StateEvent) => void;
  /**
   * Pasta (URL) com as máscaras de cratera `<ballId>.mask`, para quando o manifest de artes não as traz (em produção elas vêm junto com o site).
   * Sem máscara em lugar nenhum, a cratera vira um círculo.
   */
  craterMaskBase?: string;
}

/** Constante de tempo (ms) com que a inclinação do personagem alcança a do chão. */
const TILT_SMOOTHING_MS = 90;
/** O corpo geométrico do plano B. Os pés do personagem (do sprite também) ficam em `FEET_Y`, abaixo da posição do jogador. */
const BODY = { x: -15, y: -20, w: 30, h: 30 };
const FEET_Y = BODY.y + BODY.h;
/** Altura do corpo geométrico: o sprite é mais alto, e a diferença sobe nome, barra de vida e marcador. */
const BODY_HEIGHT = BODY.h - 6;
/** Deslocamento mínimo (px por quadro) para o personagem contar como andando. */
const MOVING_EPSILON = 0.05;
/** Quanto do verde e do azul o sprite perde quando acabou de ser ferido (1 = fica todo vermelho). */
const HURT_TINT_STRENGTH = 0.6;

interface PlayerNode {
  root: Container;
  body: Graphics;
  /** Camadas do visual do personagem; só existe depois que a arte carrega (até lá vale o corpo geométrico). */
  skin: Container | null;
  /** Chave das folhas que o `skin` mostra; muda quando a aparência do jogador muda. */
  skinKey: string;
  skinOwner?: CharacterSkin;
  /** Já foi visto morto (a cova nasce uma vez). */
  dead: boolean;
  /** Quanto o fantasma já subiu do ponto onde o corpo morreu (px). */
  ghostLift: number;
  /** Tempo desde que o jogador desistiu (ms): ele some aos poucos em vez de virar fantasma. */
  leaveMs: number;
  anim: CharacterAnimator;
  /** Posição e vida do quadro anterior: a diferença diz se o personagem andou ou levou dano. */
  last: { x: number; y: number; blood: number };
  /** Inclinação desenhada (rad), suavizada em direção à do chão. */
  tilt: number;
  barrel: Graphics;
  /** Arma na mão durante o disparo (`shootMovie` da bomba): filha do `skin`, na mesma célula e pose do corpo, por cima das folhas. */
  weaponSprite: Sprite;
  /** Asa esquerda (atrás do corpo) e direita (à frente): contêineres irmãos do `skin`, com a mesma pose dele. */
  wingBack: Container;
  wingFront: Container;
  /** Pasta da asa que os dois contêineres mostram (vazio = nenhuma). */
  wingPic: string;
  /** Tempo batendo asas (ms): zera ao parar, para o bater recomeçar do primeiro quadro. */
  wingClock: number;
  name: Text;
  blood: Graphics;
  marker: Graphics;
}

/** Número de dano/cura subindo da cabeça de um combatente. */
interface FloatingText {
  node: Container;
  accountId: string;
  x: number;
  y: number;
  ageMs: number;
  /** Posição do slot na pilha do mesmo alvo: números simultâneos não se sobrepõem. */
  slot: number;
  big: boolean;
}

/** Raio (px) da mira desenhada em volta do personagem e altura do centro dela acima dos pés. */
const AIM_RADIUS = 74;
/** Distância do centro do personagem até onde a seta, a marca do tiro anterior e a linha do chão começam: a mira nasce afastada do boneco, em vez de sobre a cabeça dele. */
const AIM_GAP = 34;

/** Quanto o número fica na tela (ms), quanto sobe (px) e a altura da cabeça do personagem. */
const FLOAT_DURATION_MS = 1500;
const FLOAT_RISE = 64;
const FLOAT_HEAD_Y = 96;

/** Altura (px) em que o fantasma flutua acima da cova (o legado põe a alma 70 px acima do corpo). */
const GHOST_LIFT = 70;
const GHOST_RISE_MS = 500;
/** Quanto o voo espera a arte da bomba chegar antes de usar o projétil simples (tempo da trajetória, em ms). */
const ART_WAIT_MS = 1200;
/** Aceleração da cova caindo (px/s²). */
const TOMB_GRAVITY = 1500;
/** A bandeira de quem desiste entra um pouco no chão, para a haste não parecer solta. */
const FLAG_SINK = 2;
/** Quem desiste some aos poucos (ms), em vez de virar fantasma. */
const LEAVE_FADE_MS = 450;
/** A bandeira branca de quem desiste sobe da haste ao tamanho cheio (ms). */
const FLAG_RISE_MS = 450;
/** Altura da haste da bandeira (px) e tamanho do pano. */
const FLAG_POLE = 72;
const FLAG_CLOTH = 34;
const FLAG_CLOTH_HEIGHT = 22;

interface Tomb {
  sprite: Sprite;
  /** Ponto da cova no mapa (o `pos` do `TombView`): nasce na posição do jogador. */
  x: number;
  y: number;
  vy: number;
  /** Caindo: anda para baixo até a caixa tocar o chão. Parada, só uma explosão por perto a acorda. */
  falling: boolean;
}

/** Bandeira branca fincada onde o jogador desistiu. No lugar da cova: quem desiste não morre. */
interface SurrenderFlag {
  ageMs: number;
  x: number;
  baseY: number;
  graphics: Graphics;
}

interface Explosion {
  x: number;
  y: number;
  radius: number;
  ageMs: number;
  durationMs: number;
  graphics: Graphics;
  /** Explosão com a arte da bomba: o quadro troca a ${BLAST_FPS} por segundo. Sem arte, vale o círculo (`graphics`). */
  sprite?: Sprite;
  frames?: Texture[];
}

/**
 * Desenha a partida: fundo, terreno destrutível (a arte do mapa com as crateras apagadas), jogadores, projétil e explosões.
 * Só lê o `GameState`; nunca decide nada do jogo (o servidor é a autoridade). A arte de personagem do legado (composição de camadas
 * de equipamento) ainda está na lista de retrabalho manual do pipeline (4.6): aqui o jogador usa o visual default (sem itens) e,
 * enquanto a arte não carrega ou se ela faltar, um corpo geométrico com a cor do time.
 */
export class GameRenderer implements MinimapSource {
  private readonly world = new Container();
  private readonly backLayer = new Container();
  private readonly foreLayer = new Container();
  private readonly playerLayer = new Container();
  private readonly fxLayer = new Container();
  private readonly floatLayer = new Container();
  private readonly floats: FloatingText[] = [];
  private readonly projectile = new Graphics();
  private readonly trail: { x: number; y: number }[] = [];
  private readonly players = new Map<string, PlayerNode>();
  private readonly explosions: Explosion[] = [];
  private foreCanvas: HTMLCanvasElement | null = null;
  private foreTexture: Texture | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private readonly camera = new Camera();
  private minimap: Minimap | null = null;
  /** Aparência de cada jogador, informada pela tela (que conhece o catálogo de itens). Sem ela vale o visual padrão. */
  private readonly looks = new Map<string, PlayerLook>();
  /** Textura da cova (`asset.game.tombAsset` do legado); sem ela, morrer não deixa cova. */
  private tombTexture: Texture | null = null;
  private readonly tombs: Tomb[] = [];
  private readonly flags: SurrenderFlag[] = [];
  private bombArt: BombArtLibrary | null = null;
  private wingArt: WingArtLibrary | null = null;
  /** Projétil com a arte da bomba da arma; sem arte, o desenho simples (`projectile`) continua valendo. */
  private readonly bulletSprite = new Sprite();
  /** Rastro do projétil (`FlyingPartical` da bomba): as partículas ficam atrás do projétil e terminam a vida depois do impacto. */
  private readonly particleLayer = new Container();
  private readonly particles = new ParticleSystem();
  private readonly particleSprites: Sprite[] = [];
  private particleArt: ParticleArtLibrary | null = null;
  private flyEmitter: Emitter | null = null;
  private flyShot: unknown = null;
  private particleClockMs = 0;
  private readonly skins = new Map<string, Promise<CharacterSkin | null>>();
  private readonly ground = new GroundSampler();
  private terrainRevision = 0;
  /** Último tiro e último turno vistos: um tiro novo ou um turno novo devolvem a câmera ao acompanhamento automático. */
  private lastShot: unknown = null;
  private lastTurn = -1;
  private loadedMapId = -1;
  private destroyed = false;
  private followAccountId = '';
  /** Quem joga neste navegador: só a mira dele é desenhada (a dos outros jogadores nunca aparece). */
  private localAccountId = '';
  /** Ângulo absoluto do último tiro do jogador local, mostrado como referência na mira. */
  private previousAim: number | null = null;
  private insetBottom = 0;

  private constructor(
    private readonly app: Application,
    private readonly options: RendererOptions,
  ) {
    this.world.addChild(this.backLayer, this.foreLayer, this.playerLayer, this.fxLayer, this.floatLayer);
    this.fxLayer.addChild(this.particleLayer, this.projectile, this.bulletSprite);
    this.bulletSprite.anchor.set(0.5);
    this.bulletSprite.visible = false;
    if (options.assets) {
      // As bibliotecas são as mesmas da tela de confronto, que já as aqueceu (`preloadMatch`).
      const art = sharedArt(options.assets);
      this.particleArt = art.particleArt;
      this.bombArt = art.bombArt;
      this.wingArt = art.wingArt;
    }
    this.app.stage.addChild(this.world);
  }

  static async create(options: RendererOptions): Promise<GameRenderer> {
    const app = new Application();
    const target = options.resizeTarget ?? options.canvas.parentElement ?? options.canvas;
    await app.init({
      canvas: options.canvas,
      width: Math.max(320, target.clientWidth),
      height: Math.max(240, target.clientHeight),
      background: 0x0a0e19,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });
    const renderer = new GameRenderer(app, options);
    renderer.observeSize(target);
    void renderer.loadTomb();
    app.ticker.add((ticker) => renderer.tick(ticker.deltaMS));
    return renderer;
  }

  private async loadTomb(): Promise<void> {
    const assets = this.options.assets;
    if (!assets) return;
    try {
      const url = await (await UiAtlas.load(assets, 'game'))?.blob('asset.game.tombAsset');
      const bitmap = url ? await loadBitmap(url) : null;
      if (bitmap && !this.destroyed) this.tombTexture = Texture.from(bitmap);
    } catch {
      this.tombTexture = null;
    }
  }

  /** Cova que nasce onde o jogador morreu e cai até o chão (ou para fora do mapa), como a `TombView` do legado. */
  private spawnTomb(x: number, y: number): void {
    if (!this.tombTexture) return;
    const sprite = new Sprite(this.tombTexture);
    // Gira em torno do ponto (0, 3) da cova, que na imagem é (15, 40).
    sprite.pivot.set(-TOMB_IMAGE_OFFSET.x, -TOMB_IMAGE_OFFSET.y + 3);
    sprite.position.set(x, y + 3);
    this.playerLayer.addChildAt(sprite, 0);
    this.tombs.push({ sprite, x, y, vy: 0, falling: true });
  }

  /** `SimpleBomb.bomb`: toda cova a menos de 100 px da explosão volta a cair, para acompanhar a cratera que abriu embaixo dela. */
  private wakeTombs(x: number, y: number): void {
    for (const t of this.tombs) {
      if (Math.hypot(t.x - x, t.y - y) <= TOMB_WAKE_RADIUS) t.falling = true;
    }
  }

  /** Bandeira branca de quem desistiu: fincada no chão onde ele estava (ou na altura dos pés, se não há chão embaixo). */
  private spawnSurrenderFlag(x: number, y: number): void {
    const surface = this.ground.surfaceBelow(x, y - 2);
    const graphics = new Graphics();
    this.playerLayer.addChild(graphics);
    this.flags.push({ ageMs: 0, x, baseY: (surface ?? y + FEET_Y) + FLAG_SINK, graphics });
  }

  private syncTombs(deltaMs: number): void {
    if (!this.ground.ready) return;
    const dt = deltaMs / 1000;
    for (let i = this.tombs.length - 1; i >= 0; i--) {
      const t = this.tombs[i]!;
      if (t.falling) {
        t.vy += TOMB_GRAVITY * dt;
        const step = fallTomb((x, y) => this.ground.solidAt(x, y), t.x, t.y, t.y + t.vy * dt);
        t.y = step.y;
        if (step.landed) {
          t.falling = false;
          t.vy = 0;
        }
        if (t.falling && t.y > this.options.state.mapHeight + 200) {
          t.sprite.destroy();
          this.tombs.splice(i, 1);
          continue;
        }
        // Parou (ou ainda cai): na queda a imagem fica reta; ao parar, gira para o chão (`stopMoving`).
        t.sprite.rotation = t.falling ? 0 : this.ground.tilt(t.x, t.y);
      }
      t.sprite.position.set(t.x, t.y + 3);
    }
  }

  /** A haste sobe do chão e o pano abre e ondula na ponta dela. */
  private syncFlags(deltaMs: number): void {
    for (const f of this.flags) {
      f.ageMs += deltaMs;
      const k = Math.min(1, f.ageMs / FLAG_RISE_MS);
      const rise = 1 - (1 - k) ** 3;
      const top = f.baseY - FLAG_POLE * rise;
      const g = f.graphics;
      g.clear();
      g.moveTo(f.x, f.baseY).lineTo(f.x, top).stroke({ color: 0x5a3a1a, width: 4, cap: 'round' });
      g.circle(f.x, top - 2, 3.5).fill(0xe0a93e);
      // O pano é uma faixa de três segmentos que ondula de ponta a ponta.
      const w = FLAG_CLOTH * rise;
      const h = FLAG_CLOTH_HEIGHT * rise;
      const upper: number[] = [f.x + 2, top];
      const lower: number[] = [];
      for (let i = 1; i <= 3; i++) {
        const dy = Math.sin(f.ageMs / 140 + i) * 2.5 * i;
        upper.push(f.x + 2 + (w * i) / 3, top + dy);
        lower.unshift(f.x + 2 + (w * i) / 3, top + h + dy);
      }
      g.poly([...upper, ...lower, f.x + 2, top + h]).fill(0xffffff).stroke({ color: 0x9aa4b2, width: 1.5 });
    }
  }

  get viewWidth(): number {
    return this.app.screen.width;
  }

  get viewHeight(): number {
    return this.app.screen.height;
  }

  /** Faixa da parte de baixo que a interface cobre (painel de controles): o mapa e os jogadores ficam acima dela. */
  /** Define quem é o jogador local: só ele vê a própria mira. */
  setLocalPlayer(accountId: string): void {
    this.localAccountId = accountId;
  }

  /** Ângulo absoluto (0 a 360) do tiro anterior do jogador local, ou `null`; aparece como linha na mira e fica fixo mesmo que o chão mude de inclinação. */
  setPreviousAim(angle: number | null): void {
    this.previousAim = angle;
  }

  setInsets(bottom: number): void {
    this.insetBottom = Math.max(0, Math.min(bottom, this.viewHeight / 2));
  }

  /** Quem a câmera acompanha quando não há tiro no ar (normalmente o jogador da vez). */
  follow(accountId: string): void {
    this.followAccountId = accountId;
  }

  get state(): GameState {
    return this.options.state;
  }

  get terrainVersion(): number {
    return this.terrainRevision;
  }

  /** O terreno desenhado (arte ou máscara, com as crateras), para o minimapa reduzir. */
  terrainImage(): CanvasImageSource | null {
    return this.foreCanvas;
  }

  /** Posição do projétil em reprodução, em coordenadas do mapa. */
  projectilePoint(): Point | null {
    return this.options.state.playback ? this.followPoint : null;
  }

  /** Região do mapa que a câmera mostra agora, em coordenadas do mapa. */
  viewRect(): Rect {
    return this.camera.viewRect();
  }

  /** Navegação manual: centra a câmera no ponto do mapa até o próximo tiro, o próximo turno ou `resumeFollow`. */
  panTo(x: number, y: number): void {
    this.camera.panTo(x, y);
    this.applyCamera();
  }

  beginCameraDrag(): void {
    this.camera.beginDrag();
  }

  endCameraDrag(): void {
    this.camera.endDrag();
  }

  /** Devolve a câmera ao acompanhamento automático (por exemplo, quando o próprio jogador anda). */
  resumeFollow(): void {
    this.camera.resumeFollow();
  }

  /** Liga o minimapa a um canvas da interface; ele é desenhado a cada quadro e destruído junto com o renderizador. */
  attachMinimap(canvas: HTMLCanvasElement): Minimap {
    this.minimap?.destroy();
    this.minimap = new Minimap(canvas, this);
    return this.minimap;
  }

  /** Carrega a arte do mapa do estado atual e reaplica as crateras já abertas. */
  async loadMap(): Promise<void> {
    const state = this.options.state;
    const assets = this.options.assets;
    this.backLayer.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.foreLayer.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.foreTexture?.destroy(true);
    this.foreTexture = null;
    this.foreCanvas = null;
    this.ground.reset();
    this.loadedMapId = state.mapId;
    this.minimap?.cancelDrag();

    const w = state.mapWidth;
    const h = state.mapHeight;
    const fallback = new Graphics().rect(0, 0, w, h).fill(0x1a2240);
    this.backLayer.addChild(fallback);

    const art = assets?.mapArt(state.mapId);
    if (assets && art?.back) {
      const back = await cachedBitmap(assets.url(art.back));
      if (back && this.loadedMapId === state.mapId) {
        const sprite = new Sprite(Texture.from(back));
        sprite.width = w;
        sprite.height = h;
        this.backLayer.addChild(sprite);
      }
    }
    if (assets && art?.fore) {
      const fore = await cachedBitmap(assets.url(art.fore));
      if (fore && this.loadedMapId === state.mapId) {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d', { willReadFrequently: true })!.drawImage(fore, 0, 0, w, h);
        this.foreCanvas = canvas;
        this.ground.refresh(canvas, 0, 0, w, h);
        this.foreTexture = Texture.from(canvas);
        this.foreLayer.addChild(new Sprite(this.foreTexture));
      }
    } else if (assets) {
      // Sem arte do terreno publicada: desenha a própria máscara de colisão (sólido em marrom).
      await this.drawMaskFallback(assets.mapMaskPath(state.mapId));
    }

    for (const crater of state.craters) await this.eraseCrater(crater);
    this.terrainRevision++;
    this.snapCamera();
  }

  /** Apaga a cratera do terreno desenhado. Sem máscara da cratera, apaga um círculo. */
  /** Máscara da cratera da bomba: a do manifest de artes ou, se ele não a traz, a que o site serve em `craterMaskBase`. */
  private async craterMask(ballId: number): Promise<TerrainMask | null> {
    const assets = this.options.assets;
    return assets ? loadCraterMask(assets, ballId, this.options.craterMaskBase) : null;
  }

  /** Baixa antes do primeiro tiro a arte (projétil e explosão), a máscara da cratera e a borda das bombas da arma. */
  private readonly preloaded = new Set<number>();
  private preloadWeapon(weaponTemplateId: number, visualBallIds: readonly number[] = []): void {
    // O estágio visual troca só o desenho (projétil e explosão): a cratera continua a da bomba da arma.
    for (const ballId of visualBallIds) {
      if (this.preloaded.has(ballId)) continue;
      this.preloaded.add(ballId);
      void this.bombArt?.load(ballId);
    }
    for (const ballId of ballsOfWeapon(weaponTemplateId)) {
      if (this.preloaded.has(ballId)) continue;
      this.preloaded.add(ballId);
      void this.bombArt?.load(ballId);
      void this.craterMask(ballId);
    }
    if (this.options.assets && this.preloaded.size > 0) {
      void sharedArt(this.options.assets).brinks.load();
    }
  }

  async eraseCrater(crater: CraterInfo): Promise<void> {
    const assets = this.options.assets;
    const mask = await this.craterMask(crater.ballId);
    const canvas = this.foreCanvas;
    if (!canvas) return;
    if (assets) {
      await sharedArt(assets).brinks.load();
    }
    const ctx = canvas.getContext('2d')!;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    if (mask) {
      const stamp = document.createElement('canvas');
      stamp.width = mask.width;
      stamp.height = mask.height;
      stamp.getContext('2d')!.putImageData(new ImageData(mask.toRgba(), mask.width, mask.height), 0, 0);
      ctx.drawImage(stamp, crater.x - Math.trunc(mask.width / 2), crater.y - Math.trunc(mask.height / 2));
    } else {
      ctx.beginPath();
      ctx.arc(crater.x, crater.y, FALLBACK_CRATER_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    let size = mask ? { w: mask.width, h: mask.height } : { w: FALLBACK_CRATER_RADIUS * 2, h: FALLBACK_CRATER_RADIUS * 2 };
    const brink = assets ? sharedArt(assets).brinks.image(crater.ballId) : null;
    if (brink) {
      // Como no legado: a borda só aparece onde o terreno continua depois do buraco (borda × alfa do terreno) e é pintada sobre ele.
      const bx = crater.x - Math.trunc(brink.width / 2);
      const by = crater.y - Math.trunc(brink.height / 2);
      const layer = document.createElement('canvas');
      layer.width = brink.width;
      layer.height = brink.height;
      const layerCtx = layer.getContext('2d')!;
      layerCtx.drawImage(brink, 0, 0);
      layerCtx.globalCompositeOperation = 'destination-in';
      layerCtx.drawImage(canvas, bx, by, brink.width, brink.height, 0, 0, brink.width, brink.height);
      ctx.drawImage(layer, bx, by);
      size = { w: Math.max(size.w, brink.width), h: Math.max(size.h, brink.height) };
    }
    this.ground.refresh(canvas, crater.x - Math.trunc(size.w / 2), crater.y - Math.trunc(size.h / 2), size.w, size.h);
    this.foreTexture?.source.update();
    this.terrainRevision++;
  }

  /** Converte um ponto da tela (coordenadas do cliente) para o mapa, desfazendo câmera e zoom. */
  clientToWorld(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.options.canvas.getBoundingClientRect();
    return { x: (clientX - rect.left - this.world.x) / this.camera.scale, y: (clientY - rect.top - this.world.y) / this.camera.scale };
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.minimap?.destroy();
    this.minimap = null;
    this.resizeObserver?.disconnect();
    this.foreTexture?.destroy(true);
    this.app.destroy({ removeView: false }, { children: true });
  }

  private async drawMaskFallback(path: string): Promise<void> {
    try {
      const mask = await this.options.assets!.mask(path);
      const canvas = document.createElement('canvas');
      canvas.width = mask.width;
      canvas.height = mask.height;
      canvas.getContext('2d', { willReadFrequently: true })!.putImageData(new ImageData(mask.toRgba(120, 84, 52), mask.width, mask.height), 0, 0);
      this.foreCanvas = canvas;
      this.ground.refresh(canvas, 0, 0, canvas.width, canvas.height);
      this.foreTexture = Texture.from(canvas);
      this.foreLayer.addChild(new Sprite(this.foreTexture));
    } catch {
      // Sem máscara também: fica só o fundo liso.
    }
  }

  private observeSize(target: HTMLElement): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.destroyed) return;
      this.app.renderer.resize(Math.max(320, target.clientWidth), Math.max(240, target.clientHeight));
    });
    this.resizeObserver.observe(target);
  }

  private tick(deltaMs: number): void {
    if (this.destroyed) return;
    const state = this.options.state;
    if (state.status === 'idle') return;
    if (this.loadedMapId !== state.mapId) {
      void this.loadMap();
    }

    for (const event of state.update(deltaMs)) {
      if (event.type === 'impact') {
        const shot = event.playback.shot;
        if (shot.impact) this.addExplosion(shot.impact.x, shot.impact.y, shot.radius || 60, shot.ballId);
        // A cratera só sai do terreno desenhado depois que a máscara dela chega; as covas por perto caem quando o chão já mudou.
        const at = shot.impact;
        const wake = () => {
          if (at) this.wakeTombs(at.x, at.y);
        };
        if (event.crater) void this.eraseCrater(event.crater).then(wake, wake);
        else wake();
        for (const text of damageTexts(shot.damages)) this.addFloat(text);
      } else if (event.type === 'skillUsed') {
        const heal = healText(event.skill);
        if (heal) this.addFloat(heal);
      }
      this.options.onEvent?.(event);
    }

    this.syncPlayers(deltaMs);
    this.syncProjectile();
    this.syncParticles(deltaMs);
    this.syncExplosions(deltaMs);
    this.syncTombs(deltaMs);
    this.syncFlags(deltaMs);
    this.syncFloats(deltaMs);
    this.watchCameraEvents();
    this.updateCamera(deltaMs);
    this.minimap?.draw();
  }

  private syncPlayers(deltaMs: number): void {
    const state = this.options.state;
    const seen = new Set<string>();
    for (const c of state.combatantList()) {
      seen.add(c.accountId);
      let node = this.players.get(c.accountId);
      if (!node) {
        node = this.createPlayer(c);
        this.preloadWeapon(c.weaponTemplateId, c.visualBallIds);
        this.players.set(c.accountId, node);
        this.playerLayer.addChild(node.root);
      }
      this.drawPlayer(node, c, state.turn?.accountId === c.accountId && state.status === 'playing', deltaMs);
    }
    for (const [id, node] of this.players) {
      if (!seen.has(id)) {
        node.root.destroy({ children: true });
        this.players.delete(id);
      }
    }
  }

  private createPlayer(c: CombatantView): PlayerNode {
    const root = new Container();
    const body = new Graphics();
    const barrel = new Graphics();
    const blood = new Graphics();
    const marker = new Graphics();
    const name = new Text({ text: c.nickName, style: { fill: 0xffffff, fontSize: 13, fontFamily: 'system-ui, sans-serif', stroke: { color: 0x000000, width: 3 } } });
    name.anchor.set(0.5, 1);
    const wingBack = new Container();
    const wingFront = new Container();
    // Ordem: sombra do time, asa de trás, [skin, inserido pela `dressPlayer`], asa da frente, cano, textos.
    root.addChild(body, wingBack, wingFront, barrel, blood, name, marker);
    return { root, body, skin: null, skinKey: '', dead: !c.alive, ghostLift: c.alive || c.forfeited ? 0 : GHOST_LIFT, leaveMs: c.forfeited && !c.alive ? LEAVE_FADE_MS : 0, anim: new CharacterAnimator(Math.random() * 900), last: { x: c.displayX, y: c.displayY, blood: c.blood }, tilt: 0, barrel, weaponSprite: new Sprite(), wingBack, wingFront, wingPic: '', wingClock: 0, name, blood, marker };
  }

  /** Define a aparência de um jogador (peças de visual e arma). Chame de novo quando o catálogo trouxer mais peças. */
  setLook(accountId: string, look: PlayerLook): void {
    this.looks.set(accountId, look);
    if (look.wingPic) void this.wingArt?.load(look.wingPic); // baixa a asa antes de o jogador aparecer com ela
  }

  /** Folhas da aparência do jogador, ou as do visual padrão enquanto ele não tem aparência. */
  private layersOf(accountId: string, sex: boolean): string[] {
    const assets = this.options.assets!;
    const look = this.looks.get(accountId) ?? { sex: sex ? 'm' : 'f', worn: [], weaponPic: '' };
    return gameLayerPaths(look, (path) => assets.has(path));
  }

  /** Mostra no jogador as folhas da aparência dele; carrega as que faltam e, até chegarem, mantém as que já estavam na tela. */
  private dressPlayer(node: PlayerNode, c: CombatantView): void {
    if (!this.options.assets) return;
    const key = skinKey(this.layersOf(c.accountId, c.sex));
    if (key === node.skinKey) return;
    node.skinKey = key;
    const paths = key ? key.split('|') : [];
    let pending = this.skins.get(key);
    if (!pending) {
      pending = CharacterSkin.load(this.options.assets, c.sex ? 'm' : 'f', paths);
      this.skins.set(key, pending);
    }
    void pending.then((skin) => {
      if (this.destroyed || !skin || node.skinKey !== key || node.root.destroyed) return;
      node.weaponSprite.parent?.removeChild(node.weaponSprite); // a arma na mão não morre junto com a folha antiga
      node.skin?.destroy({ children: true });
      node.skin = skin.create();
      node.skinOwner = skin;
      node.root.addChildAt(node.skin, 2); // acima da sombra do time (`body`) e da asa de trás, abaixo da asa da frente, do cano e dos textos
    });
  }

  private skinOf(node: PlayerNode): CharacterSkin | null {
    return node.skinOwner ?? null;
  }

  private drawPlayer(node: PlayerNode, c: CombatantView, active: boolean, deltaMs: number): void {
    node.root.position.set(c.displayX, c.displayY);
    this.dressPlayer(node, c);
    if (!c.alive && !node.dead) {
      node.dead = true;
      // Quem desiste não morre: deixa só a bandeira, e a cova fica para quem morreu.
      if (c.forfeited) this.spawnSurrenderFlag(c.displayX, c.displayY);
      else this.spawnTomb(c.displayX, c.displayY);
    }
    if (c.forfeited && !c.alive) {
      // Desistiu: o personagem some no lugar, sem virar fantasma.
      node.leaveMs = Math.min(LEAVE_FADE_MS, node.leaveMs + deltaMs);
      node.root.alpha = 1 - node.leaveMs / LEAVE_FADE_MS;
      node.root.visible = node.root.alpha > 0;
    } else if (!c.alive) {
      node.ghostLift = Math.min(GHOST_LIFT, node.ghostLift + (GHOST_LIFT * deltaMs) / GHOST_RISE_MS);
    }
    node.body.clear();
    node.barrel.clear();
    node.blood.clear();
    node.marker.clear();

    const moved = Math.abs(c.displayX - node.last.x) > MOVING_EPSILON || Math.abs(c.displayY - node.last.y) > MOVING_EPSILON;
    const wounded = c.blood < node.last.blood;
    node.last = { x: c.displayX, y: c.displayY, blood: c.blood };

    let lift = 0; // quanto nome, barra de vida e marcador sobem acima do corpo geométrico
    if (node.skin) lift = this.drawSprite(node, c, deltaMs, moved, wounded);
    else this.drawGeometricBody(node, c);
    node.name.position.set(0, BODY.y - 18 - lift);
    node.name.alpha = !c.alive ? 0.6 : c.connected ? 1 : 0.5;
    if (!c.alive) return;

    // A mira é só de quem joga neste navegador, e só na vez dele (a dos outros jogadores não é desenhada).
    if (active && c.accountId === this.localAccountId && !this.options.state.playback) this.drawAim(node.barrel, c);

    const ratio = c.maxBlood > 0 ? Math.max(0, Math.min(1, c.blood / c.maxBlood)) : 0;
    const barY = BODY.y - 8 - lift;
    node.blood.rect(-20, barY, 40, 5).fill(0x0a0e19);
    node.blood.rect(-20, barY, 40 * ratio, 5).fill(ratio > 0.6 ? 0x3ddc97 : ratio > 0.25 ? 0xffb020 : 0xff5d73);

    if (active) {
      node.marker.poly([-6, BODY.y - 46 - lift, 6, BODY.y - 46 - lift, 0, BODY.y - 36 - lift]).fill(0xffb020);
    }
  }

  /**
   * Mira do jogador local, em palitos de 5 em 5 graus ao longo da faixa da arma (0° = a inclinação do chão sob ele), com a linha pontilhada do
   * tiro anterior e a seta do ângulo atual.
   */
  private drawAim(g: Graphics, c: CombatantView): void {
    const cx = 0;
    const cy = BODY.y + 8;
    const R = AIM_RADIUS;
    const groundAngle = aimAngle(0, c.facing, c.tilt);
    const groundFrom = polar(cx, cy, groundAngle, AIM_GAP);
    const ground = polar(cx, cy, groundAngle, R + 12);
    g.moveTo(groundFrom.x, groundFrom.y).lineTo(ground.x, ground.y).stroke({ color: 0xffffff, width: 1.5, alpha: 0.5 });
    for (const t of aimTicks(c.angleRange, c.facing, c.tilt)) {
      const from = polar(cx, cy, t.angle, R - (t.major ? 15 : 8));
      const to = polar(cx, cy, t.angle, R);
      g.moveTo(from.x, from.y).lineTo(to.x, to.y).stroke({ color: 0x0a0e19, width: t.major ? 5 : 4, cap: 'butt' });
      g.moveTo(from.x, from.y).lineTo(to.x, to.y).stroke({ color: t.major ? 0xd9fff0 : 0xb8ffcf, width: t.major ? 3 : 2, cap: 'butt' });
    }
    // A mira atual coincide com a do tiro anterior (os dois são ângulos absolutos).
    const same = this.previousAim !== null && Math.abs(c.aim - this.previousAim) < 0.5;
    if (this.previousAim !== null) {
      // Tiro anterior: faixa larga e opaca (bem visível); no mesmo ângulo ela ganha brilho e um anel na ponta.
      const previous = this.previousAim;
      const from = polar(cx, cy, previous, AIM_GAP);
      const end = polar(cx, cy, previous, R + 2);
      if (same) g.moveTo(from.x, from.y).lineTo(end.x, end.y).stroke({ color: 0xffd25a, width: 14, alpha: 0.35, cap: 'round' });
      g.moveTo(from.x, from.y).lineTo(end.x, end.y).stroke({ color: 0x0a0e19, width: same ? 9 : 8, alpha: 0.8, cap: 'round' });
      g.moveTo(from.x, from.y).lineTo(end.x, end.y).stroke({ color: same ? 0xffe58a : 0xffd25a, width: same ? 6 : 5, alpha: same ? 1 : 0.7, cap: 'round' });
      const tip = polar(cx, cy, previous, R + 6);
      g.circle(tip.x, tip.y, same ? 8 : 5.5).fill(0xffd25a).stroke({ color: 0x0a0e19, width: 2 });
      if (same) g.circle(tip.x, tip.y, 13).stroke({ color: 0xfff6cf, width: 2, alpha: 0.9 });
    }
    const tip = polar(cx, cy, c.aim, R + 4);
    const base = polar(cx, cy, c.aim, AIM_GAP);
    g.moveTo(base.x, base.y).lineTo(tip.x, tip.y).stroke({ color: 0x0a0e19, width: 6, cap: 'round' });
    g.moveTo(base.x, base.y).lineTo(tip.x, tip.y).stroke({ color: same ? 0xfff6cf : 0xff5a4a, width: 3.5, cap: 'round' });
    const head = polar(cx, cy, c.aim, R + 14);
    const left = polar(tip.x, tip.y, c.aim + 150, 8);
    const right = polar(tip.x, tip.y, c.aim - 150, 8);
    g.poly([head.x, head.y, left.x, left.y, right.x, right.y]).fill(same ? 0xfff6cf : 0xff5a4a).stroke({ color: 0x0a0e19, width: 1.5 });
  }

  /**
   * A arma na mão enquanto o jogador atira (`GamePlayer.weaponMovie` do legado): o clipe `shootMovie` da bomba, desenhado na célula do corpo
   * (`weaponX/weaponY` e o canto do corpo são o mesmo ponto) e por cima dele. Sem arte ainda, sem clipe ou fora do disparo, some.
   */
  private syncWeaponMovie(node: PlayerNode, sprite: Container, ballId: number | undefined, frame: number | null): void {
    const texture = ballId !== undefined && frame !== null ? this.bombArt?.peek(ballId)?.weapon?.[frame - 1] : undefined;
    node.weaponSprite.visible = texture !== undefined;
    if (!texture) return;
    node.weaponSprite.texture = texture;
    if (node.weaponSprite.parent !== sprite) sprite.addChild(node.weaponSprite);
  }

  /**
   * Asas do jogador (`GamePlayer.addWing` do legado): a esquerda atrás do corpo e a direita à frente, a partir do canto da célula (`WING_ANCHOR`),
   * com a mesma pose do corpo (inclinação, giro para o lado em que olha). Paradas no quadro de espera, ou no de tiro enquanto ele atira; ao andar ou
   * atirar elas batem (`movie.play()` em `GameCharacter.doAction`, parado em `stand`). Somem quando ele morre (`switchWingVisible(isLiving)`).
   */
  private syncWings(node: PlayerNode, c: CombatantView, sprite: Container, firing: boolean, moving: boolean, deltaMs: number): void {
    const pic = c.alive ? (this.looks.get(c.accountId)?.wingPic ?? '') : '';
    const art = pic ? this.wingArt?.peek(pic) : null;
    if (!art) {
      node.wingBack.visible = node.wingFront.visible = false;
      return;
    }
    if (node.wingPic !== pic) {
      node.wingBack.removeChildren().forEach((child) => child.destroy());
      node.wingFront.removeChildren().forEach((child) => child.destroy());
      const holderBack = new Container();
      const holderFront = new Container();
      holderBack.addChild(new Sprite(art.left.still[0]));
      holderFront.addChild(new Sprite(art.right.still[0]));
      node.wingBack.addChild(holderBack);
      node.wingFront.addChild(holderFront);
      node.wingPic = pic;
      node.wingClock = 0;
    }
    const flapping = firing || moving;
    node.wingClock = flapping ? node.wingClock + deltaMs : 0;
    const state = firing ? 1 : 0;
    const place = (part: Container, side: WingSide): void => {
      const holder = part.children[0] as Container;
      const wing = holder.children[0] as Sprite;
      const flap = side.flap;
      if (flapping && flap) {
        const m = flap.matrix[Math.min(state, flap.matrix.length - 1)]!;
        holder.setFromMatrix(new Matrix(m[0], m[1], m[2], m[3], m[4], m[5]));
        wing.texture = flap.frames[Math.floor((node.wingClock * WING_FPS) / 1000) % flap.frames.length]!;
        wing.position.set(WING_ANCHOR.x + flap.at[0], WING_ANCHOR.y + flap.at[1]);
      } else {
        holder.setFromMatrix(new Matrix());
        wing.texture = side.still[state]!;
        wing.position.set(WING_ANCHOR.x + side.at[0], WING_ANCHOR.y + side.at[1]);
      }
    };
    place(node.wingBack, art.left);
    place(node.wingFront, art.right);
    for (const part of [node.wingBack, node.wingFront]) {
      part.visible = true;
      part.pivot.copyFrom(sprite.pivot);
      part.position.copyFrom(sprite.position);
      part.scale.copyFrom(sprite.scale);
      part.rotation = sprite.rotation;
      part.tint = sprite.tint;
    }
  }

  /** Plano B sem a arte do personagem: caixa na cor do time (cinza se morto). */
  private drawGeometricBody(node: PlayerNode, c: CombatantView): void {
    if (!c.alive) {
      node.body.roundRect(BODY.x + 4, BODY.y + 8, BODY.w - 8, BODY.h - 8, 4).fill(0x6b7280).stroke({ color: 0x1f2937, width: 2 });
      return;
    }
    node.body.roundRect(BODY.x, BODY.y + 6, BODY.w, BODY.h - 6, 8).fill(TEAM_COLORS[c.team] ?? 0xffffff).stroke({ color: 0x0a0e19, width: 2 });
    node.body.circle(c.facing >= 0 ? 6 : -6, BODY.y + 14, 3).fill(0xffffff);
  }

  /** Aplica ao sprite a pose do quadro (quadro, postura, escala, inclinação do chão, realce de dano) e desenha a sombra do time. Devolve quanto nome e barra sobem. */
  private drawSprite(node: PlayerNode, c: CombatantView, deltaMs: number, moved: boolean, wounded: boolean): number {
    const skin = this.skinOf(node)!;
    const sprite = node.skin!;
    if (wounded) node.anim.hit();
    const playback = this.options.state.playback;
    const firing = playback?.shot.shooterId === c.accountId;
    const ballId = firing ? playback?.shot.ballId : undefined;
    const pose = node.anim.update(deltaMs, { alive: c.alive, moving: moved, firing, ballId });

    skin.show(sprite, pose.frame, pose.posture.footX);
    this.syncWeaponMovie(node, sprite, ballId, pose.weaponFrame);
    sprite.scale.set(c.facing >= 0 ? 1 : -1, pose.rise);
    sprite.tint = hurtTint(pose.hurt);

    // Acompanha o chão, com suavização para não tremer ao andar pela borda dos pixels.
    const target = c.alive ? this.ground.tilt(c.displayX, c.displayY) : 0;
    node.tilt += (target - node.tilt) * (1 - Math.exp(-deltaMs / TILT_SMOOTHING_MS));
    // Como no legado, o boneco gira em torno do ponto do servidor (a origem do `GamePlayer`), e não dos pés: os pés ficam a `FEET_Y` dele, no sentido da rotação.
    // Girar pelos pés deixava a cabeça dentro do morro quando o boneco olha para baixo numa rampa íngreme.
    const reach = FEET_Y + pose.bobY - node.ghostLift;
    sprite.position.set(-Math.sin(node.tilt) * reach, Math.cos(node.tilt) * reach);
    sprite.rotation = node.tilt;
    this.syncWings(node, c, sprite, firing, moved, deltaMs);
    node.body.pivot.set(0, FEET_Y);
    node.body.position.set(-Math.sin(node.tilt) * FEET_Y, Math.cos(node.tilt) * FEET_Y);
    node.body.rotation = node.tilt;
    node.body.ellipse(0, FEET_Y, 18, 5).fill({ color: c.alive ? (TEAM_COLORS[c.team] ?? 0xffffff) : 0x6b7280, alpha: 0.7 });

    return pose.posture.height - BODY_HEIGHT + node.ghostLift;
  }

  private syncProjectile(): void {
    const state = this.options.state;
    const playback = state.playback;
    this.projectile.clear();
    this.bulletSprite.visible = false;
    if (!playback) {
      this.trail.length = 0;
      this.stopFlyEmitter();
      return;
    }
    // A arte da bomba começa a baixar quando o tiro chega, antes de o projétil sair da mão.
    const art = this.bombArt?.peek(playback.shot.ballId) ?? null;
    // Antes do arremesso o projétil ainda não saiu da mão do atirador.
    if (!playback.released) return;
    const pos = positionAt(playback.shot.trajectory, state.playbackElapsedMs());
    if (pos.done) {
      this.stopFlyEmitter();
      return;
    }
    // Com a arte ainda baixando, o projétil espera (até `ART_WAIT_MS`) em vez de aparecer como bolinha e trocar no meio do voo.
    if (this.bombArt && art === null && state.playbackElapsedMs() < ART_WAIT_MS) {
      this.followPoint = { x: pos.x, y: pos.y };
      return;
    }
    this.followPoint = { x: pos.x, y: pos.y };
    this.followFlyEmitter(playback.shot, pos);
    if (art?.bullet) {
      // O projétil da arma aponta para a direita na arte e gira junto com a trajetória.
      this.bulletSprite.texture = art.bullet;
      this.bulletSprite.position.set(pos.x, pos.y);
      // Como o legado: a bomba que gira (SpinV > 0) gira a cada passo de 40 ms e vira para o lado do tiro; a que não gira só inverte em Y quando vai para a esquerda.
      const dir = playback.shot.trajectory.length > 1 && playback.shot.trajectory[1]!.x < playback.shot.trajectory[0]!.x ? -1 : 1;
      const spin = ballSpin(playback.shot.ballId);
      this.bulletSprite.scale.set(spin ? dir : 1, spin ? 1 : dir);
      this.bulletSprite.angle = pos.angle * (180 / Math.PI) + (spin ? spinDegrees(spin[0], spin[1], dir, Math.floor(state.playbackElapsedMs() / 40)) : 0);
      this.bulletSprite.visible = true;
      return;
    }
    this.trail.push({ x: pos.x, y: pos.y });
    if (this.trail.length > 14) this.trail.shift();
    this.trail.forEach((p, i) => {
      this.projectile.circle(p.x, p.y, 2 + (i / this.trail.length) * 3).fill({ color: 0xffd27a, alpha: (i + 1) / (this.trail.length + 2) });
    });
    this.projectile.circle(pos.x, pos.y, playback.shot.special ? 8 : 6).fill(playback.shot.special ? 0xff5d73 : 0xffb020).stroke({ color: 0x1a1200, width: 2 });
  }

  private followPoint: { x: number; y: number } | null = null;

  /** `SimpleBomb.startMoving` e `moveTo`: o emissor da bomba nasce com o projétil e acompanha a posição e o ângulo dele. */
  private followFlyEmitter(shot: object, pos: { x: number; y: number; angle: number }): void {
    if (this.flyShot !== shot) {
      this.flyShot = shot;
      this.stopFlyEmitter();
      const def = EMITTERS[BALL_FLYING[(shot as { ballId: number }).ballId] ?? -1];
      if (def) this.flyEmitter = this.particles.addEmitter(def);
    }
    if (this.flyEmitter) {
      this.flyEmitter.x = pos.x;
      this.flyEmitter.y = pos.y;
      this.flyEmitter.angle = pos.angle;
    }
  }

  /** `SimpleBomb.stopMoving`: o emissor sai, e as partículas já soltas terminam a vida. */
  private stopFlyEmitter(): void {
    this.flyEmitter?.remove();
    this.flyEmitter = null;
  }

  /** Avança a simulação em passos de 40 ms do tempo do tiro (o tempo real vezes `PROJECTILE_SPEED`) e desenha as partículas. */
  private syncParticles(deltaMs: number): void {
    if (this.particles.active) {
      this.particleClockMs = Math.min(this.particleClockMs + deltaMs * PROJECTILE_SPEED, PARTICLE_STEP * 1000 * 5);
      while (this.particleClockMs >= PARTICLE_STEP * 1000) {
        this.particleClockMs -= PARTICLE_STEP * 1000;
        this.particles.step();
      }
    } else {
      this.particleClockMs = 0;
    }
    const live = this.particles.particles;
    let used = 0;
    for (const p of live) {
      const shape = this.particleArt?.peek(p.def.shape);
      if (!shape) continue;
      let sprite = this.particleSprites[used];
      if (!sprite) {
        sprite = new Sprite();
        sprite.blendMode = 'add';
        this.particleSprites.push(sprite);
        this.particleLayer.addChild(sprite);
      }
      used++;
      sprite.visible = true;
      sprite.texture = shape.texture;
      sprite.anchor.set(shape.anchor.x, shape.anchor.y);
      sprite.position.set(p.x, p.y);
      sprite.scale.set(p.scale);
      sprite.rotation = p.rotation;
      sprite.alpha = Math.max(0, Math.min(1, p.alpha));
      // O legado soma a cor ao desenho da forma (`keepColor`) ou pinta a forma inteira; sem cor a forma sai como é.
      sprite.tint = p.def.keepColor && p.color === 0 ? 0xffffff : p.color;
    }
    for (let i = used; i < this.particleSprites.length; i++) this.particleSprites[i]!.visible = false;
  }

  private addExplosion(x: number, y: number, radius: number, ballId: number): void {
    const graphics = new Graphics();
    this.fxLayer.addChild(graphics);
    const blast = this.bombArt?.peek(ballId)?.blast ?? null;
    if (blast) {
      const sprite = new Sprite(blast.frames[0]);
      sprite.anchor.set(blast.anchor.x, blast.anchor.y);
      sprite.position.set(x, y);
      this.fxLayer.addChild(sprite);
      this.explosions.push({ x, y, radius, ageMs: 0, durationMs: (blast.frames.length / BLAST_FPS) * 1000, graphics, sprite, frames: blast.frames });
      return;
    }
    this.explosions.push({ x, y, radius, ageMs: 0, durationMs: 450, graphics });
  }

  /** Cria o número flutuante; ele nasce na cabeça do alvo e acompanha o personagem enquanto sobe. */
  private addFloat(text: DamageText): void {
    const style = DAMAGE_TEXT_STYLES[text.kind];
    const node = new Container();
    const value = new Text({
      text: text.text,
      style: {
        fill: style.fill,
        fontSize: style.big ? 52 : 32,
        fontFamily: '"Lilita One", Impact, "Arial Black", sans-serif',
        fontWeight: '700',
        stroke: { color: style.stroke, width: style.big ? 9 : 6, join: 'round' },
      },
    });
    value.anchor.set(0.5);
    node.addChild(value);
    if (style.big) {
      const seal = new Text({
        text: 'CRÍTICO',
        style: { fill: style.fill, fontSize: 15, fontWeight: '700', fontFamily: 'system-ui, sans-serif', letterSpacing: 2, stroke: { color: style.stroke, width: 4, join: 'round' } },
      });
      seal.anchor.set(0.5);
      seal.y = 32;
      node.addChild(seal);
    }
    const slot = this.floats.filter((f) => f.accountId === text.accountId).length;
    this.floatLayer.addChild(node);
    this.floats.push({ node, accountId: text.accountId, x: 0, y: 0, ageMs: 0, slot, big: style.big });
  }

  private syncFloats(deltaMs: number): void {
    const state = this.options.state;
    for (let i = this.floats.length - 1; i >= 0; i--) {
      const f = this.floats[i]!;
      f.ageMs += deltaMs;
      const t = Math.min(1, f.ageMs / FLOAT_DURATION_MS);
      const target = state.me(f.accountId);
      if (target) {
        f.x = target.displayX;
        f.y = target.displayY;
      }
      const rise = FLOAT_RISE * (1 - Math.pow(1 - Math.min(1, f.ageMs / 600), 3)) + t * 14;
      const pop = f.ageMs < 140 ? 1 + (1 - f.ageMs / 140) * 0.55 : 1;
      f.node.position.set(f.x, f.y - FLOAT_HEAD_Y - rise - f.slot * (f.big ? 52 : 34));
      f.node.scale.set(pop);
      f.node.alpha = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
      if (t >= 1) {
        f.node.destroy({ children: true });
        this.floats.splice(i, 1);
      }
    }
  }

  private syncExplosions(deltaMs: number): void {
    for (let i = this.explosions.length - 1; i >= 0; i--) {
      const e = this.explosions[i]!;
      e.ageMs += deltaMs;
      const t = Math.min(1, e.ageMs / e.durationMs);
      e.graphics.clear();
      if (e.sprite && e.frames) {
        e.sprite.texture = e.frames[Math.min(e.frames.length - 1, Math.floor((e.ageMs / 1000) * BLAST_FPS))]!;
      } else {
        e.graphics.circle(e.x, e.y, e.radius * (0.3 + 0.7 * t)).fill({ color: 0xffb020, alpha: 0.55 * (1 - t) });
        e.graphics.circle(e.x, e.y, e.radius * t).stroke({ color: 0xffffff, width: 3, alpha: 1 - t });
      }
      if (t >= 1) {
        e.sprite?.destroy();
        e.graphics.destroy();
        this.explosions.splice(i, 1);
      }
    }
  }

  private cameraTarget(): Point {
    const state = this.options.state;
    const playback = state.playback;
    if (!playback) this.followPoint = null;
    const at = (c: CombatantView | undefined): Point | null => (c ? { x: c.displayX, y: c.displayY - 20 } : null);
    return cameraFocus({
      playback,
      projectile: this.followPoint,
      shooter: playback ? at(state.combatants.get(playback.shot.shooterId)) : null,
      turnPlayer: at(state.combatants.get(this.followAccountId || state.turn?.accountId || '')),
      mapCenter: { x: state.mapWidth / 2, y: state.mapHeight / 2 },
    });
  }

  /** Um tiro novo ou um turno novo encerram a navegação manual (a câmera volta a seguir o combate). */
  private watchCameraEvents(): void {
    const state = this.options.state;
    // Durante o preparo do arremesso nada muda para a câmera: tiro e turno novos só valem quando o projétil sai.
    const preparing = state.playback !== null && !state.playback.released;
    const shot = state.playback?.released ? state.playback.shot : null;
    const turn = preparing ? this.lastTurn : (state.turn?.number ?? -1);
    if ((shot !== null && shot !== this.lastShot) || turn !== this.lastTurn) this.camera.resumeFollow();
    this.lastShot = shot;
    this.lastTurn = turn;
  }

  /** Área da tela livre para o mapa: a faixa do painel de controles fica de fora. */
  private updateBounds(): void {
    const state = this.options.state;
    this.camera.setBounds({ width: state.mapWidth, height: state.mapHeight }, { width: this.viewWidth, height: this.viewHeight - this.insetBottom });
  }

  private snapCamera(): void {
    this.updateBounds();
    this.camera.snapTo(this.cameraTarget());
    this.lastShot = this.options.state.playback?.shot ?? null;
    this.lastTurn = this.options.state.turn?.number ?? -1;
    this.applyCamera();
  }

  private updateCamera(deltaMs: number): void {
    this.updateBounds();
    this.camera.follow(this.cameraTarget(), deltaMs);
    this.applyCamera();
  }

  /** A câmera só move o contêiner do mundo: as coordenadas dos personagens, do projétil e das crateras não mudam. */
  private applyCamera(): void {
    const offset = this.camera.offset();
    this.world.scale.set(this.camera.scale);
    this.world.position.set(offset.x, offset.y);
  }
}

/** Cor do sprite: branco normal, avermelhado (menos verde e azul) logo depois de um golpe. */
function hurtTint(hurt: number): number {
  const channel = Math.round(255 * (1 - hurt * HURT_TINT_STRENGTH));
  return (0xff << 16) | (channel << 8) | channel;
}
